import React, { useState, useRef, useEffect } from 'react';
import {
  MDBCard,
  MDBCardBody,
  MDBCardHeader,
  MDBBtn,
  MDBIcon,
  MDBSpinner,
  MDBBadge,
  MDBInput,
  MDBCollapse,
  MDBRow,
  MDBCol,
  MDBTextArea,
} from 'mdb-react-ui-kit';
import useChatbot from './useChatbot';
import SourceCard from './SourceCard';
import { NAMESPACE_OPTIONS } from './chatbotConfig';

// Simple markdown-to-HTML converter for RAG answers
function markdownToHtml(text) {
  if (!text) return '';
  let html = text
    // Escape HTML
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Headers
    .replace(/^### (.+)$/gm, '<h6 class="mt-2 mb-1">$1</h6>')
    .replace(/^## (.+)$/gm, '<h5 class="mt-2 mb-1">$1</h5>')
    .replace(/^# (.+)$/gm, '<h4 class="mt-2 mb-1">$1</h4>')
    // List items
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    // Source references [1], [2] etc. as badges
    .replace(
      /\[(\d+)\]/g,
      '<span class="badge badge-dark rounded-pill" style="font-size:0.65rem;vertical-align:super;cursor:default">$1</span>'
    )
    // Line breaks
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');

  // Wrap consecutive <li> in <ul>
  html = html.replace(/((?:<li>.*?<\/li><br\/>?)+)/g, (match) => {
    const cleaned = match.replace(/<br\/?>/g, '');
    return `<ul class="mb-1 ps-3">${cleaned}</ul>`;
  });

  return html;
}

function MessageBubble({ message }) {
  if (message.role === 'user') {
    return (
      <div className="d-flex justify-content-end mb-3">
        <div
          className="bg-primary text-white rounded-4 px-3 py-2"
          style={{ maxWidth: '80%' }}
        >
          {message.content}
        </div>
      </div>
    );
  }

  if (message.role === 'error') {
    return (
      <div className="d-flex justify-content-start mb-3">
        <div
          className="bg-danger bg-opacity-10 text-danger border border-danger rounded-4 px-3 py-2"
          style={{ maxWidth: '80%' }}
        >
          <MDBIcon fas icon="exclamation-triangle" className="me-2" />
          {message.content}
        </div>
      </div>
    );
  }

  // Assistant message
  return (
    <div className="d-flex justify-content-start mb-3">
      <div style={{ maxWidth: '90%', width: '100%' }}>
        <div
          className="bg-light rounded-4 px-3 py-2 mb-2"
          dangerouslySetInnerHTML={{ __html: markdownToHtml(message.content) }}
        />

        {/* Metadata */}
        {message.metadata && (
          <div className="mb-2">
            {message.metadata.processingTimeMs && (
              <small className="text-muted me-3">
                <MDBIcon fas icon="clock" className="me-1" />
                {(message.metadata.processingTimeMs / 1000).toFixed(1)}s
              </small>
            )}
            {message.metadata.totalHits > 0 && (
              <small className="text-muted me-3">
                <MDBIcon fas icon="search" className="me-1" />
                {message.metadata.totalHits} Treffer
              </small>
            )}
            {message.metadata.model && (
              <small className="text-muted">
                <MDBIcon fas icon="robot" className="me-1" />
                {message.metadata.model}
              </small>
            )}
          </div>
        )}

        {/* Source cards */}
        {message.resolvedSources && message.resolvedSources.length > 0 && (
          <MDBRow>
            {message.resolvedSources.map((source) => (
              <MDBCol md="6" key={source.sourceIndex}>
                <SourceCard source={source} sourceIndex={source.sourceIndex} />
              </MDBCol>
            ))}
          </MDBRow>
        )}

        {/* Loading sources */}
        {message.sources &&
          Object.keys(message.sources).length > 0 &&
          !message.resolvedSources && (
            <div className="text-muted small">
              <MDBSpinner size="sm" className="me-2" />
              Quellen werden geladen...
            </div>
          )}
      </div>
    </div>
  );
}

export default function AdminChatbot() {
  const {
    messages,
    isLoading,
    error,
    selectedNamespace,
    setSelectedNamespace,
    namespaceKeys,
    setNamespaceKey,
    maxSources,
    setMaxSources,
    sendQuery,
    clearChat,
  } = useChatbot();

  const [inputText, setInputText] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(
    () => !namespaceKeys.public && !namespaceKeys.premium
  );
  const messagesEndRef = useRef(null);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!inputText.trim() || isLoading) return;
    sendQuery(inputText);
    setInputText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="p-3" style={{ maxWidth: 900, margin: '0 auto' }}>
      <h4 className="mb-3">
        <MDBIcon fas icon="robot" className="me-2" />
        AI Chatbot (RAG)
      </h4>

      {/* Settings Panel */}
      <MDBCard className="mb-3">
        <MDBCardHeader
          className="d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer' }}
          onClick={() => setSettingsOpen(!settingsOpen)}
        >
          <span>
            <MDBIcon fas icon="cog" className="me-2" />
            Settings
          </span>
          <div className="d-flex align-items-center gap-2">
            <MDBBadge color={namespaceKeys[selectedNamespace] ? 'success' : 'danger'}>
              {NAMESPACE_OPTIONS.find((o) => o.value === selectedNamespace)?.label || selectedNamespace}
            </MDBBadge>
            <MDBIcon fas icon={settingsOpen ? 'chevron-up' : 'chevron-down'} />
          </div>
        </MDBCardHeader>
        <MDBCollapse open={settingsOpen}>
          <MDBCardBody>
            <MDBRow>
              <MDBCol md="6" className="mb-3">
                <label className="form-label fw-bold">Namespace</label>
                <select
                  className="form-select"
                  value={selectedNamespace}
                  onChange={(e) => setSelectedNamespace(e.target.value)}
                >
                  {NAMESPACE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </MDBCol>
              <MDBCol md="6" className="mb-3">
                <MDBInput
                  label="Max Sources"
                  type="number"
                  min="1"
                  max="20"
                  value={maxSources}
                  onChange={(e) => setMaxSources(e.target.value)}
                />
              </MDBCol>
            </MDBRow>
            <MDBRow>
              <MDBCol md="4" className="mb-3">
                <MDBInput
                  label="Public Key"
                  type="password"
                  value={namespaceKeys.public}
                  onChange={(e) => setNamespaceKey('public', e.target.value)}
                />
              </MDBCol>
              <MDBCol md="4" className="mb-3">
                <MDBInput
                  label="Premium Key"
                  type="password"
                  value={namespaceKeys.premium}
                  onChange={(e) => setNamespaceKey('premium', e.target.value)}
                />
              </MDBCol>
              <MDBCol md="4" className="mb-3">
                <MDBInput
                  label="Federation Key"
                  type="password"
                  value={namespaceKeys.federation}
                  onChange={(e) => setNamespaceKey('federation', e.target.value)}
                />
              </MDBCol>
            </MDBRow>
            <small className="text-muted">
              Keys werden lokal im Browser gespeichert (localStorage).
            </small>
          </MDBCardBody>
        </MDBCollapse>
      </MDBCard>

      {/* Error */}
      {error && (
        <div className="alert alert-warning py-2 mb-3">
          <MDBIcon fas icon="exclamation-triangle" className="me-2" />
          {error}
        </div>
      )}

      {/* Chat Area */}
      <MDBCard>
        <MDBCardBody>
          {/* Messages */}
          <div
            style={{
              height: 'calc(100vh - 420px)',
              minHeight: 300,
              overflowY: 'auto',
              paddingRight: 8,
            }}
          >
            {messages.length === 0 && (
              <div className="text-center text-muted py-5">
                <MDBIcon fas icon="comments" size="3x" className="mb-3 d-block" />
                <p>Stelle eine Frage an den RAG-Chatbot.</p>
                <small>
                  z.B. "Welche Unternehmen bieten Reinraumtechnik an?" oder
                  "Was sind die neuesten Produkte?"
                </small>
              </div>
            )}

            {messages.map((msg, idx) => (
              <MessageBubble key={idx} message={msg} />
            ))}

            {isLoading && (
              <div className="d-flex justify-content-start mb-3">
                <div className="bg-light rounded-4 px-3 py-2">
                  <MDBSpinner size="sm" className="me-2" />
                  Antwort wird generiert...
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <hr className="my-2" />

          {/* Input Area */}
          <div className="d-flex gap-2 align-items-end">
            <div className="flex-grow-1">
              <MDBTextArea
                label="Frage eingeben..."
                rows={1}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
              />
            </div>
            <MDBBtn
              color="primary"
              onClick={handleSend}
              disabled={isLoading || !inputText.trim()}
              className="flex-shrink-0"
            >
              <MDBIcon fas icon="paper-plane" />
            </MDBBtn>
            <MDBBtn
              color="light"
              onClick={clearChat}
              disabled={messages.length === 0}
              className="flex-shrink-0"
              title="Chat leeren"
            >
              <MDBIcon fas icon="trash" />
            </MDBBtn>
          </div>
        </MDBCardBody>
      </MDBCard>
    </div>
  );
}
